import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { IoPeopleSharp } from "react-icons/io5";
import { LiaFileInvoiceSolid } from "react-icons/lia";
import { MdDescription } from "react-icons/md";
import { RiVisaFill } from "react-icons/ri";
import { SlPeople } from "react-icons/sl";
import { useNavigate } from "react-router-dom";
import "./ApplicationPage.scss";

const theme = createTheme({
  typography: {
    fontFamily: "Poppins, Arial, sans-serif",
  },
});

const getGreeting = () => {
  const hour = new Date().getHours();
  return hour < 12
    ? "Good morning"
    : hour < 16
      ? "Good afternoon"
      : "Good evening";
};

export default function ApplicationPage() {
  const navigate = useNavigate();
  const cardRefs = useRef([]);
  const [fetchedApps, setFetchedApps] = useState([]);
  const [username, setUsername] = useState("");
  const [profileImageSrc, setProfileImageSrc] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [clearedAppData, setClearedAppData] = useState(false);

  const getApplications = async () => {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": `${process.env.REACT_APP_BACKEND_URL_LOGIN_APP}`,
        "Access-Control-Allow-Credentials": "true",
      },
      withCredentials: true,
    };
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL_LOGIN_APP}/admin/applications`,
        config,
      )
      .then((res) => {
        const { applications } = res?.data;
        console.log("applications from req", applications);
        setFetchedApps(applications);
      })
      .catch((error) => {
        console.log("Error fetching applications : ", error);
      });
  };

  const getUserDetails = async () => {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": `${process.env.REACT_APP_BACKEND_URL_LOGIN_APP}`,
        "Access-Control-Allow-Credentials": "true",
      },
      withCredentials: true,
    };
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL_LOGIN_APP}/auth/user-details`,
        config,
      )
      .then((res) => {
        let userDetails = res?.data;
        setUsername(userDetails.username);
        // FIX: use strict equality (===) instead of == for is_superadmin check
        if (userDetails.is_superadmin === 1) {
          setIsSuperAdmin(true);
        }
        if (userDetails.img_url) {
          const base64Image = `data:image/png;base64,${userDetails.img_url}`;
          setProfileImageSrc(base64Image);
        }
      })
      .catch((error) => {
        console.log("Error fetching user details : ", error);
      });
  };

  const handleOfferLetterClick = () => {
    window.location.href = process.env.REACT_APP_FRONTEND_URL_OFFER_LETTER;
  };

  const handleVisaTracker = () => {
    window.location.href = process.env.REACT_APP_FRONTEND_URL_VISA_TRACKER;
  };

  const handleSOWTracker = () => {
    window.location.href = process.env.REACT_APP_FRONTEND_URL_SOW_TRACKER;
  };

  const handleInvoiceTracker = () => {
    window.location.href = process.env.REACT_APP_FRONTEND_URL_INVOICE_TRACKER;
  };

  const handleTPATracker = () => {
    window.location.href = process.env.REACT_APP_FRONTEND_URL_TPA_TRACKER;
  };

  const handleMSATracker = () => {
    window.location.href = process.env.REACT_APP_FRONTEND_URL_MSA_TRACKER;
  };

  const handleInsuranceTracker = () => {
    window.location.href = process.env.REACT_APP_FRONTEND_URL_INSURANCE_TRACKER;
  };

  // FIX: Added # to all color strings (were missing # prefix, e.g. "007BFF" → "#007BFF")
  const appEnhancements = {
    1: {
      icon: <SlPeople size={30} style={{ color: "#007BFF" }} />,
      onClick: handleSOWTracker,
    },
    2: {
      icon: <IoPeopleSharp size={30} style={{ color: "#007BFF" }} />,
      onClick: handleTPATracker,
    },
    3: {
      icon: <LiaFileInvoiceSolid size={30} style={{ color: "#007BFF" }} />,
      onClick: handleInvoiceTracker,
    },
    4: {
      icon: <RiVisaFill size={30} style={{ color: "#007BFF" }} />,
      onClick: handleVisaTracker,
    },
    5: {
      icon: <MdDescription size={30} style={{ color: "#007BFF" }} />,
      onClick: handleOfferLetterClick,
    },
    7: {
      icon: <MdDescription size={30} style={{ color: "#007BFF" }} />,
      onClick: handleMSATracker,
    },
    8: {
      icon: <MdDescription size={30} style={{ color: "#007BFF" }} />,
      onClick: handleInsuranceTracker,
    },
  };

  // FIX: Convert app_id to Number before lookup — API may return string IDs,
  // causing appEnhancements["8"] to miss the numeric key 8.
  // FIX: Added fallback icon and onClick so future apps never render broken cards.
  const enhancedApps = fetchedApps?.map((app) => {
    const appId = Number(app.app_id);
    const enhancement = appEnhancements[appId];
    console.log("APP:", appId, app.app_name, enhancement);
    return {
      ...app,
      icon:
        enhancement?.icon || (
          <MdDescription size={30} style={{ color: "#007BFF" }} />
        ),
      onClick:
        enhancement?.onClick ||
        (() => console.warn("No handler for app:", appId)),
    };
  });

  const handleLogoutClick = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL_LOGIN_APP}/auth/logout`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      if (response.ok) {
        navigate("/login");
      } else {
        console.error("Logout failed");
      }
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const greeting = getGreeting();

  useEffect(() => {
    checkAuthentication();
    getApplications();
    getUserDetails();
  }, []);

  function clearAllAppData() {
    if (!clearedAppData) {
      document.cookie.split(";").forEach((cookie) => {
        const name = cookie.split("=")[0].trim();
        let domain = "";

        if (
          process.env.REACT_APP_ENV === "production" ||
          process.env.REACT_APP_ENV === "test"
        ) {
          domain = "; domain=.pharmaaceapps.com";
        }

        document.cookie =
          name + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/" + domain;
      });

      localStorage.clear();
      sessionStorage.clear();

      indexedDB.databases().then((databases) => {
        databases.forEach((db) => {
          indexedDB.deleteDatabase(db.name);
        });
      });

      setClearedAppData(true);
    }
  }

  const checkAuthentication = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL_LOGIN_APP}/auth/check-token`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (response.ok) {
        // stay on ApplicationPage
      } else {
        clearAllAppData();
        navigate("/login");
        console.error("Not Logged in");
      }
    } catch (error) {
      clearAllAppData();
      console.error("Error during authentication check:", error);
    }
  };

  // Mouse hover gradient tracking
  useEffect(() => {
    const cursor = document.querySelector(".bg__gradient");
    let mouseX = 0,
      mouseY = 0;
    let cursorX = 0,
      cursorY = 0;
    let speed = 0.01;

    const animate = () => {
      const distX = mouseX - cursorX;
      const distY = mouseY - cursorY;

      cursorX += distX * speed;
      cursorY += distY * speed;

      if (cursor) {
        cursor.style.left = cursorX + "px";
        cursor.style.top = cursorY + "px";
      }

      requestAnimationFrame(animate);
    };

    animate();

    const mouseMoveHandler = (event) => {
      mouseX = event.pageX;
      mouseY = event.pageY;
    };

    document.addEventListener("mousemove", mouseMoveHandler);

    return () => {
      document.removeEventListener("mousemove", mouseMoveHandler);
    };
  }, []);

  const handleCardMouseMove = (e, index) => {
    const card = cardRefs.current[index];
    const rect = card.getBoundingClientRect();
    const buffer = 10;

    const x = Math.min(
      Math.max(e.clientX - rect.left, buffer),
      rect.width - buffer,
    );
    const y = Math.min(
      Math.max(e.clientY - rect.top, buffer),
      rect.height - buffer,
    );

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const tiltX = ((x - centerX) / centerX) * 15;
    const tiltY = ((y - centerY) / centerY) * -15;

    card.style.transform = `perspective(1000px) rotateX(${tiltY}deg) rotateY(${tiltX}deg)`;
  };

  const handleCardMouseLeave = (index) => {
    const card = cardRefs.current[index];
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
  };

  const handleUserControlButton = () => {
    navigate("/user-control");
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid container className="main-container">
        <Container className="welcome-page">
          <Grid container alignItems="end" justifyContent="space-between">
            <Grid item>
              <h1 className="heading-name-1">
                PACE<span className="heading-name-2">APPS</span>
              </h1>
            </Grid>
            <Grid item>
              <Grid className="logos-container" container spacing={0.4}>
                <Grid item>
                  {isSuperAdmin ? (
                    <Button
                      className="user-control-button"
                      variant="outlined"
                      onClick={handleUserControlButton}
                    >
                      User Control
                    </Button>
                  ) : null}
                </Grid>
                <Grid item>
                  <IconButton>
                    {profileImageSrc ? (
                      <img
                        src={profileImageSrc}
                        alt="profile_image"
                        style={{
                          borderRadius: "50%",
                          // FIX: width/height must be strings with units, not bare numbers
                          width: "30px",
                          height: "30px",
                        }}
                      />
                    ) : (
                      <AccountCircleIcon
                        style={{
                          // FIX: fontSize must be a string with unit
                          fontSize: "30px",
                          color: "grey",
                        }}
                      />
                    )}
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleLogoutClick}>
                    <LogoutIcon
                      style={{ color: "#282828", marginTop: "3px" }}
                    />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Typography
            variant="h4"
            sx={{ fontWeight: "bold" }}
            className="welcome-page__greeting"
          >
            {greeting}!{" "}
            <span className="welcome-page__name"> {username} </span>
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold" }}
            className="welcome-page__subtext"
          >
            Where do you want to go today?
          </Typography>

          <Grid
            container
            justifyContent="center"
            spacing={3}
            className="welcome-page__grid"
          >
            {enhancedApps?.map((app, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={app.app_id ?? index}>
                <Card
                  ref={(el) => (cardRefs.current[index] = el)}
                  className="welcome-page__card"
                  onMouseMove={(e) => handleCardMouseMove(e, index)}
                  onMouseLeave={() => handleCardMouseLeave(index)}
                  onClick={app.onClick}
                >
                  <CardContent className="welcome-page__card-content">
                    <span className="welcome-page__card-icon">{app.icon}</span>
                    <Typography
                      variant="h6"
                      className="welcome-page__card-title"
                    >
                      {app.app_name}
                    </Typography>
                    <Typography variant="body2">{app.description}</Typography>
                  </CardContent>
                  <span className="welcome-page__card-bottom-logo">
                    <EastOutlinedIcon style={{ color: "#007BFF" }} />
                  </span>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Grid>
    </ThemeProvider>
  );
}